/** Canonical prefix for in-shell tab routes (Ionic per-tab stacks). */
export const TABS_PREFIX = '/tabs';

export const PUBLIC_PROFILE_PATH_PATTERN = `${TABS_PREFIX}/leads/public/:workspaceUsername/:username`;

/** Canonical in-shell paths. Use these for navigation, links, and redirects. */
export const shellPaths = {
  home: `${TABS_PREFIX}/home`,
  profiles: `${TABS_PREFIX}/profiles`,
  profilesEdit: `${TABS_PREFIX}/profiles/edit`,
  profilesLinkCard: `${TABS_PREFIX}/profiles/link-card`,
  leads: `${TABS_PREFIX}/leads`,
  /** Events hub on the Leads tab stack (list → detail via `leadEventDetail`). */
  leadsEvents: `${TABS_PREFIX}/leads/events`,
  leadsPublicProfilePath: (workspaceUsername: string, username: string) =>
    `${TABS_PREFIX}/leads/public/${encodeURIComponent(workspaceUsername)}/${encodeURIComponent(username)}`,
  leadEventDetail: (id: string) => `${TABS_PREFIX}/leads/events/${id}`,
  leadDetail: (id: string) => `${TABS_PREFIX}/leads/${id}`,
  more: `${TABS_PREFIX}/more`,
  moreHub: `${TABS_PREFIX}/more/hub`,
  moreUserSettings: `${TABS_PREFIX}/more/user/settings`,
  moreUserSettingsPhone: `${TABS_PREFIX}/more/user/settings?section=phone`,
  moreWhatsNew: `${TABS_PREFIX}/more/whats-new`,
  moreUserBilling: `${TABS_PREFIX}/more/user/billing`,
  moreUserNotifications: `${TABS_PREFIX}/more/user/notifications`,
  moreConfirmNewEmail: `${TABS_PREFIX}/more/confirm-new-email`,
  moreSupport: `${TABS_PREFIX}/more/support`,
  moreSupportImpersonate: `${TABS_PREFIX}/more/support/impersonate`,
  moreSupportPassword: `${TABS_PREFIX}/more/support/password`,
  moreBillingGrants: `${TABS_PREFIX}/more/support/billing-grants`,
  moreProducts: `${TABS_PREFIX}/more/products`,
  moreProductsSetup: `${TABS_PREFIX}/more/products/setup`,
  moreProductDetail: (productId: string) =>
    `${TABS_PREFIX}/more/products/${productId}`,
  morePeople: `${TABS_PREFIX}/more/people`,
  morePeopleTeamDetail: (id: string) =>
    `${TABS_PREFIX}/more/people/teams/${id}`,
  moreProfileTemplates: `${TABS_PREFIX}/more/profile-templates`,
  moreProfileTemplateDetail: (templateId: string) =>
    `${TABS_PREFIX}/more/profile-templates/${templateId}`,
  moreWorkspaceSettings: `${TABS_PREFIX}/more/workspace/settings`,
  moreWorkspaceBilling: `${TABS_PREFIX}/more/workspace/billing`,
  /** Plan picker / upgrade checkout — child of workspace billing. */
  moreWorkspaceBillingUpgrade: `${TABS_PREFIX}/more/workspace/billing/upgrade`,
  moreWorkspaceCreate: `${TABS_PREFIX}/more/workspace/create`,
  moreWorkspaceLocations: `${TABS_PREFIX}/more/workspace/locations`,
  moreWorkspaceLocationCreate: `${TABS_PREFIX}/more/workspace/locations/create`,
  moreWorkspaceLocationDetail: (id: string) =>
    `${TABS_PREFIX}/more/workspace/locations/${id}`,
  moreWorkspaceIntegrations: `${TABS_PREFIX}/more/workspace/integrations`,
  /** @deprecated Prefer reputationInbox — kept for redirects/deep links. */
  moreWorkspaceReviews: `${TABS_PREFIX}/more/workspace/reviews`,
  reputationRoot: `${TABS_PREFIX}/more/reputation`,
  reputationDashboard: `${TABS_PREFIX}/more/reputation/overview`,
  /** Canonical Reviews inbox URL (legacy: moreWorkspaceReviews). */
  reputationInbox: `${TABS_PREFIX}/more/reputation/inbox`,
  reputationAnalytics: `${TABS_PREFIX}/more/reputation/analytics`,
  reputationCompetitors: `${TABS_PREFIX}/more/reputation/competitors`,
  reputationSetup: `${TABS_PREFIX}/more/reputation/setup`,
  feedbackRoot: `${TABS_PREFIX}/more/feedback`,
  /** Send-first home (not the root — root redirects here). */
  feedbackHome: `${TABS_PREFIX}/more/feedback/home`,
  /** Inbox + Sent (People-style `?tab=`). */
  feedbackActivity: `${TABS_PREFIX}/more/feedback/activity`,
  /** Forms + Messages (People-style `?tab=`). */
  feedbackLibrary: `${TABS_PREFIX}/more/feedback/library`,
  feedbackCompose: `${TABS_PREFIX}/more/feedback/ask`,
  /** @deprecated Prefer feedbackActivity?tab=inbox — kept for redirects/deep links. */
  feedbackInbox: `${TABS_PREFIX}/more/feedback/inbox`,
  /** @deprecated Prefer feedbackActivity?tab=sent — kept for redirects/deep links. */
  feedbackInvites: `${TABS_PREFIX}/more/feedback/invites`,
  /** @deprecated Use feedbackCompose */
  feedbackInviteNew: `${TABS_PREFIX}/more/feedback/ask`,
  feedbackInviteDetail: (inviteId: string) =>
    `${TABS_PREFIX}/more/feedback/invites/${inviteId}`,
  /** @deprecated Prefer feedbackLibrary?tab=forms — kept for editors + redirects. */
  feedbackForms: `${TABS_PREFIX}/more/feedback/forms`,
  feedbackFormNew: `${TABS_PREFIX}/more/feedback/forms/new`,
  feedbackFormDetail: (templateId: string) =>
    `${TABS_PREFIX}/more/feedback/forms/${templateId}`,
  /** @deprecated Prefer feedbackLibrary?tab=messages — kept for editors + redirects. */
  feedbackMessages: `${TABS_PREFIX}/more/feedback/messages`,
  feedbackMessageNew: `${TABS_PREFIX}/more/feedback/messages/new`,
  feedbackMessageDetail: (templateId: string) =>
    `${TABS_PREFIX}/more/feedback/messages/${templateId}`,
} as const;

/** Legacy short URL kept for notifications and external links. */
export function legacyPublicProfilePath(
  workspaceUsername: string,
  username: string,
): string {
  return `/public-profile/${encodeURIComponent(workspaceUsername)}/${encodeURIComponent(username)}`;
}

/** True for legacy scanned public profile shim routes on the Leads tab stack. */
export function isPublicProfilePath(pathname: string): boolean {
  const path = canonicalizeShellPathname(pathname);
  const segments = path.split('/').filter(Boolean);
  return (
    segments[0] === 'tabs' &&
    segments[1] === 'leads' &&
    segments[2] === 'public' &&
    Boolean(segments[3]) &&
    Boolean(segments[4])
  );
}

/** Normalizes a path (preserves query string when present). */
export function canonicalizeShellPath(path: string): string {
  const [pathOnly, query = ''] = path.split('?');
  const normalized = pathOnly || '/';
  return query ? `${normalized}?${query}` : normalized;
}

/** Pathname only (no query), canonicalized. */
export function canonicalizeShellPathname(pathname: string): string {
  return canonicalizeShellPath(pathname).split('?')[0] ?? pathname;
}
